import { z } from 'zod';
import { paginationQuery, uuid } from './common.js';
const optText = (max) => z.string().trim().max(max).nullable().optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hhmm = z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/);
/** `MD`/`ME` sind die ungeschlechtlichen Doppel-/Einzel-Disziplinen für
 * Mini-Mannschaften (§ Nutzeranfrage "Mini Mannschaften") — siehe
 * `isMiniTeam`/`MINI_DISCIPLINE_ORDER` in lineup-rules.ts. */
export const DISCIPLINES = ['HE', 'DE', 'HD', 'DD', 'MX', 'MD', 'ME'];
export const MATCH_RESPONSE_STATUSES = ['pending', 'accepted', 'declined', 'maybe'];
// ── seasons ────────────────────────────────────────────────────────────────
export const seasonWritable = z.object({
    name: z.string().trim().min(1).max(120),
    startsOn: isoDate.nullable().optional(),
    endsOn: isoDate.nullable().optional(),
    status: z.enum(['planning', 'active', 'archived']).optional(),
});
export const addSeasonTeamRequest = z.object({
    teamId: uuid,
    league: optText(120),
});
// ── season matches ─────────────────────────────────────────────────────────
export const seasonMatchWritable = z.object({
    seasonId: uuid.nullable().optional(),
    teamId: uuid.nullable().optional(),
    league: optText(120),
    division: optText(120),
    matchDay: optText(60),
    matchDate: isoDate.nullable().optional(),
    matchTime: hhmm.nullable().optional(),
    homeTeam: optText(200),
    awayTeam: optText(200),
    homeOrAway: z.enum(['home', 'away']).nullable().optional(),
    venue: optText(200),
    venueAddress: optText(300),
    status: z.enum(['scheduled', 'played', 'cancelled', 'postponed']).optional(),
    result: optText(60),
    notes: optText(2000),
});
export const seasonMatchQuery = paginationQuery.extend({
    seasonId: uuid.optional(),
    teamId: uuid.optional(),
    /** comma-separated team ids — matches any of them; the filter dropdown on
     * the Saisonspiele page uses this instead of the single teamId above. */
    teamIds: z.string().optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    mine: z.enum(['true', 'false']).optional(),
});
// ── responses ──────────────────────────────────────────────────────────────
// No invite step: any member of any team may respond to any match directly:
// match_participants rows are created on first response, not pre-populated.
export const respondMatchRequest = z.object({
    status: z.enum(['accepted', 'declined', 'maybe']),
    memberId: uuid.optional(),
    comment: z.string().trim().max(500).optional(),
});
export const remindRequest = z.object({
    memberIds: z.array(uuid).optional(), // omit → every team member who hasn't responded yet
});
// ── line-up ────────────────────────────────────────────────────────────────
export const putLineupRequest = z.object({
    notes: optText(2000),
    slots: z
        .array(z.object({
        discipline: z.enum(DISCIPLINES),
        position: z.number().int().min(1).max(20).default(1),
        member1Id: uuid.nullable().optional(),
        member2Id: uuid.nullable().optional(),
    }))
        .max(50),
});
// ── Verlegungsassistent ───────────────────────────────────────────────────
// Mannschaftsführer (matches.manage) stellen mehrere Termin-Vorschläge zur
// Abstimmung; jeder Spieler (matches.respond) stimmt je Vorschlag mit
// Daumen hoch/runter ab; ist man sich einig, trägt ein Mannschaftsführer den
// vereinbarten Termin ein ("Geeinigt auf") — siehe docs/season.md.
export const RESCHEDULE_VOTE_VALUES = ['yes', 'no'];
export const rescheduleOptionInput = z.object({
    date: isoDate,
    time: hhmm.nullable().optional(),
});
export const startReschedulePollRequest = z.object({
    options: z.array(rescheduleOptionInput).min(1).max(10),
});
export const voteRescheduleOptionRequest = z.object({
    optionId: uuid,
    vote: z.enum(RESCHEDULE_VOTE_VALUES),
    memberId: uuid.optional(),
});
export const agreeRescheduleRequest = z.object({
    date: isoDate,
    time: hhmm.nullable().optional(),
});
//# sourceMappingURL=season.js.map